import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

/**
 * Browser-side file download helper shared by every "download this URL" action.
 *
 * With a file name, the file is fetched into a local object URL so the anchor
 * `download` attribute is honored (it is ignored for cross-origin URLs, which
 * would open the file in a tab instead). The blob is re-wrapped as
 * `application/octet-stream` so Firefox does not additionally open the
 * downloaded file in a new tab, and the object URL is revoked with a delay
 * because a synchronous revoke can abort the download in Firefox/Safari.
 * On fetch failure (CORS/network) the original URL is used directly and the
 * download relies on the blob's content-disposition.
 */
@Injectable({ providedIn: 'root' })
export class FileDownloadService {
  private readonly _document = inject(DOCUMENT);

  async download(url: string, fileName?: string): Promise<void> {
    if (!fileName) {
      this._anchorDownload(url);
      return;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = new Blob([await response.blob()], { type: 'application/octet-stream' });
      const objectUrl = URL.createObjectURL(blob);
      this._anchorDownload(objectUrl, fileName);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
    } catch {
      this._anchorDownload(url, fileName);
    }
  }

  private _anchorDownload(url: string, fileName?: string): void {
    const link = this._document.createElement('a');
    link.href = url;
    if (fileName) {
      link.download = fileName;
    }
    link.click();
    link.remove();
  }
}
